import { Button as BaseButton } from '@base-ui/react/button'
import type { ButtonHTMLAttributes, FC, ReactNode, Ref } from 'react'
import styles from './button.module.css'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  ref?: Ref<HTMLButtonElement>
  variant?: 'text' | 'contained' | 'outlined'
  color?: 'primary' | 'secondary' | 'error' | 'inherit'
  size?: 'small' | 'medium' | 'large'
  fullWidth?: boolean
  startIcon?: ReactNode
  endIcon?: ReactNode
  loading?: boolean
  loadingPosition?: 'start' | 'end' | 'center'
  /**
   * Keep keyboard focus while disabled (Base UI): `aria-disabled` and `data-disabled` instead of the native
   * attribute, so a button that turns busy under the user's focus does not drop it to the page (GH #2394).
   */
  focusableWhenDisabled?: boolean
  href?: string
  target?: string
  download?: string
  /** @deprecated Ignored - kept for MUI compat */
  component?: string
  tabIndex?: number
  autoFocus?: boolean
}

const colorMap: Record<string, string | undefined> = {
  secondary: styles.colorSecondary,
  error: styles.colorError,
  inherit: styles.colorInherit,
}

const sizeMap: Record<string, string | undefined> = {
  small: styles.sizeSmall,
  large: styles.sizeLarge,
}

export const Button: FC<ButtonProps> = ({
  variant = 'text',
  color = 'primary',
  size = 'medium',
  fullWidth,
  startIcon,
  endIcon,
  loading,
  loadingPosition,
  disabled,
  className,
  children,
  href,
  target,
  download,
  component: _component,
  ...rest
}) => {
  const classes = [
    styles.button,
    variant === 'contained' ? styles.contained : undefined,
    variant === 'outlined' ? styles.outlined : undefined,
    colorMap[color],
    sizeMap[size],
    fullWidth ? styles.fullWidth : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const isDisabled = disabled || loading

  if (href && !isDisabled) {
    return (
      <a
        className={classes}
        href={href}
        target={target}
        download={download}
        role="button"
        {...(rest as Record<string, unknown>)}
      >
        {startIcon ? <span className={styles.icon}>{startIcon}</span> : null}
        {children}
        {endIcon ? <span className={styles.icon}>{endIcon}</span> : null}
      </a>
    )
  }

  return (
    <BaseButton className={classes} disabled={isDisabled} {...rest}>
      {startIcon ? <span className={styles.icon}>{startIcon}</span> : null}
      {loading && loadingPosition === 'start' ? <span>...</span> : null}
      {children}
      {endIcon ? <span className={styles.icon}>{endIcon}</span> : null}
    </BaseButton>
  )
}
